import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { restaurantConfig } from "../config/restaurantConfig";

const NAV = restaurantConfig.ui.nav;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "backdrop-blur-xl bg-background/85 border-b border-border/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          data-testid="brand-link"
          aria-label={restaurantConfig.brand.name}
          className="flex items-center group"
        >
          <img
            src={restaurantConfig.brand.logo}
            alt={restaurantConfig.brand.logoAlt}
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
            draggable={false}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[11px] tracking-luxe uppercase font-sans transition-all duration-300 hover:scale-105 ${
                  isActive ? "text-burgundy dark:text-gold" : "text-foreground/70 hover:text-burgundy dark:hover:text-gold"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 right-0 h-px bg-burgundy dark:bg-gold"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <Link
            to="/reservation"
            data-testid="header-reserve-btn"
            className="hidden md:inline-flex items-center px-5 py-2.5 bg-burgundy text-white text-[11px] tracking-luxe uppercase hover:bg-burgundy-hover hover:scale-105 transition-all duration-300 shadow-md shadow-burgundy/20"
          >
            {restaurantConfig.ui.reserveButton}
          </Link>
          <ThemeToggle />
          <button
            data-testid="mobile-menu-toggle"
            aria-label={restaurantConfig.ui.mobileMenuToggleAria}
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-border/60 hover:border-accent-line text-foreground/80 hover:text-accent-line transition-colors"
          >
            {open ? <X className="w-4 h-4" strokeWidth={1.5} /> : <Menu className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-x-0 top-20 bottom-0 h-[calc(100vh-5rem)] bg-background/95 backdrop-blur-xl overflow-y-auto"
          >
            <motion.nav
              initial="initial"
              animate="animate"
              variants={{
                initial: {},
                animate: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
              }}
              className="flex flex-col px-8 py-12 gap-2"
            >
              {NAV.map((item) => (
                <motion.div
                  key={item.to}
                  variants={{
                    initial: { opacity: 0, x: -16 },
                    animate: { opacity: 1, x: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
                  }}
                >
                  <NavLink
                    to={item.to}
                    end={item.end ?? item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-4 border-b border-border/40 font-serif text-3xl ${
                        isActive ? "text-burgundy dark:text-gold" : "text-foreground"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                variants={{
                  initial: { opacity: 0, x: -16 },
                  animate: { opacity: 1, x: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
                }}
                className="mt-8"
              >
                <Link
                  to="/reservation"
                  data-testid="mobile-reserve-btn"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center px-8 py-4 bg-burgundy text-white text-xs tracking-luxe uppercase hover:bg-burgundy-hover transition-colors"
                >
                  {restaurantConfig.ui.reserveLongCta}
                </Link>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
